// WebSocket connection management
import { appSyncConfig } from './config.mjs';
import { subscriptions } from './queries.mjs';
import { logMessage, generateClientId, generateSubscriptionId } from './utils.mjs';
import { fetchGroup } from './api.mjs';
import { locale as i18nLocale } from 'virtual:i18n';

const CLIENT_INFO = `web/${i18nLocale.split('-')[0]}`;

/**
 * Client class for WebSocket connection to AppSync
 * Provides methods for subscribing to group changes and handling events
 */
export class Client {
    constructor() {
        this.webSocket = null;
        this.subscriptionId = null;
        this.currentGroupId = null;
        this.isConnecting = false;
        this.isConnected = false;

        // Callback functions
        this.groupDataCallback = null;
        this.errorCallback = null;
        this.socketErrorCallback = null;
        this.onFetchEndCallback = () => { };
        this.onFetchStartCallback = () => { };
    }

    /**
     * Set callback for when group data is received
     * @param {Function} callback - Function to call with group data
     */
    onGroupDataReceived(callback) {
        this.groupDataCallback = callback;
    }

    /**
     * Set callback for when an error occurs in the subscription
     * @param {Function} callback - Function to call with error information
     */
    onError(callback) {
        this.errorCallback = callback;
    }

    onFetchStart(callback) {
        this.onFetchStartCallback = callback;
    }

    onFetchEnd(callback) {
        this.onFetchEndCallback = callback;
    }

    /**
     * Set callback for when a WebSocket connection error occurs
     * @param {Function} callback - Function to call with socket error information
     */
    onSocketError(callback) {
        this.socketErrorCallback = callback;
    }

    /**
     * Connect to AppSync and subscribe to group changes
     * @param {string} groupId - ID of the group to subscribe to
     */
    async connect(groupId) {

        console.log('connecting to server', groupId);

        // If already connected to the same group with an open WebSocket, do nothing
        if (this.isConnected && this.currentGroupId === groupId && this.webSocket && this.webSocket.readyState === WebSocket.OPEN) {
            console.log(`Already connected to group ${groupId}`);
            return;
        }

        // If we have a stale connection (same group but WebSocket is closed), clean it up
        if (this.currentGroupId === groupId && this.webSocket && this.webSocket.readyState !== WebSocket.OPEN) {
            console.log('Cleaning up stale connection');
            this.disconnect();
        }

        // If connecting or already connected to a different group, disconnect first
        if (this.isConnecting || (this.isConnected && this.currentGroupId !== groupId)) {
            this.disconnect();
        }

        this.isConnecting = true;
        this.currentGroupId = groupId;
        try {
            // Fetch initial group data

            this.onFetchStartCallback();

            const groupData = await fetchGroup(groupId);
            if (groupData && this.groupDataCallback) {
                this.groupDataCallback(groupData, false); // false = initial fetch, not subscription update
            }

            // Create WebSocket connection
            const clientId = generateClientId();

            // Create the header object with all required headers
            const headerObj = {
                'Accept': 'application/json, text/javascript',
                'Content-Encoding': 'amz-1.0',
                'Content-Type': 'application/json; charset=utf-8',
                'X-Api-Key': appSyncConfig.apiKey,
                'Host': appSyncConfig.host,
                'splitpal-app-version': CLIENT_INFO,
                'splitpal-app-platform': 'web'
            };

            // Encode the header object for the URL
            const headerString = btoa(JSON.stringify(headerObj));
            const wsUrl = `wss://${appSyncConfig.host.replace('appsync-api', 'appsync-realtime-api')}/graphql?header=${headerString}&payload=e30%3D`;

            // Use only the graphql-ws protocol
            this.webSocket = new WebSocket(wsUrl, 'graphql-ws');

            // WebSocket event handlers
            this.webSocket.onopen = () => {
                console.log('WebSocket connection established');

                // Send connection_init message
                const connectionId = generateSubscriptionId();
                this.webSocket.send(JSON.stringify({
                    id: connectionId,
                    type: 'connection_init'
                }));
            };

            this.webSocket.onmessage = (event) => {
                const data = JSON.parse(event.data);

                switch (data.type) {
                    case 'connection_ack':
                        console.log('Connection acknowledged by server');

                        // Start subscription
                        this.subscriptionId = generateSubscriptionId();
                        const variables = { groupId: this.currentGroupId };

                        this.webSocket.send(JSON.stringify({
                            id: this.subscriptionId,
                            type: 'start',
                            payload: {
                                data: JSON.stringify({
                                    query: subscriptions.onGroupChange,
                                    variables: variables
                                }),
                                extensions: {
                                    authorization: {
                                        'Accept': 'application/json, text/javascript',
                                        'Content-Encoding': 'amz-1.0',
                                        'Content-Type': 'application/json; charset=utf-8',
                                        'X-Api-Key': appSyncConfig.apiKey,
                                        'Host': appSyncConfig.host,
                                        'splitpal-app-version': CLIENT_INFO,
                                        'splitpal-app-platform': 'web'
                                    }
                                }
                            }
                        }));

                        this.isConnecting = false;
                        this.isConnected = true;
                        console.log('Subscription started');
                        break;

                    case 'data':
                        const receivedData = data.payload.data;
                        if (receivedData.onGroupChange && this.groupDataCallback) {
                            const group = receivedData.onGroupChange;
                            this.groupDataCallback(group, true); // true = subscription update
                        }
                        break;

                    case 'error':
                        try {
                            const errorMessage = typeof data.payload === 'object' ?
                                (data.payload.message || JSON.stringify(data.payload)) :
                                String(data.payload);
                            console.error('Server error:', data.payload);

                            if (this.errorCallback) {
                                this.errorCallback(errorMessage);
                            }
                        } catch (e) {
                            console.error('Error parsing server error:', e, data.payload);

                            if (this.errorCallback) {
                                this.errorCallback('Unable to parse error details');
                            }
                        }
                        break;

                    case 'complete':
                        console.log('Subscription completed');
                        break;

                    default:
                        console.log('Received message:', data);
                }
            };

            this.webSocket.onerror = (event) => {
                try {
                    console.error('WebSocket error event:', event);

                    const errorInfo = {
                        eventPhase: event.eventPhase,
                        type: event.type,
                        isTrusted: event.isTrusted,
                        target: event.target ? event.target.url || 'WebSocket' : 'WebSocket'
                    };

                    if (this.socketErrorCallback) {
                        this.socketErrorCallback(errorInfo);
                    }
                } catch (e) {
                    console.error('Error in WebSocket error handler:', e);

                    if (this.socketErrorCallback) {
                        this.socketErrorCallback({ message: 'WebSocket error occurred' });
                    }
                }

                this.isConnecting = false;
                this.isConnected = false;
            };

            this.webSocket.onclose = () => {
                console.log('WebSocket connection closed');
                this.isConnecting = false;
                this.isConnected = false;
            };

        } catch (error) {

            console.error('Error connecting:', error.message);

            if (this.errorCallback) {
                this.errorCallback(error.message);
            }

            this.isConnecting = false;
            this.isConnected = false;
        } finally {
            this.onFetchEndCallback();
        }
    }

    /**
     * Disconnect from AppSync
     */
    disconnect() {
        if (this.webSocket) {
            // Stop subscription if we have one
            if (this.subscriptionId && this.webSocket.readyState === WebSocket.OPEN) {
                this.webSocket.send(JSON.stringify({
                    id: this.subscriptionId,
                    type: 'stop'
                }));
            }

            // Close WebSocket
            if (this.webSocket.readyState === WebSocket.OPEN || this.webSocket.readyState === WebSocket.CONNECTING) {
                this.webSocket.close();
            }

            this.webSocket = null;
        }

        // Always clear state
        this.subscriptionId = null;
        this.currentGroupId = null;
        this.isConnected = false;
        this.isConnecting = false;

        console.log('Disconnected from AppSync');
    }
}
